import { AxiosError } from "axios";

import {
  AnketaResponse,
  AnketaResponseError,
  AnketaSuccessResponse,
  LogState,
} from "../types";

function isAnketaError<T>(response: any): response is AnketaResponseError {
  return response && typeof response === "object" && "error" in response;
}

export const AnketaApiResponse = <T>(
  response: AnketaSuccessResponse<T | null> | AxiosError | AnketaResponseError
): AnketaResponse<T> => {
  let status: number = 500;
  let success: boolean = false;
  let data: T | null = null;
  let error: AnketaResponseError | undefined;
  let logState: LogState = "error";

  if (response instanceof AxiosError) {
    status = response.status || 500;
    success = false;
    // The useful, renderable error lives in the backend response body
    // (`{ error, details }`), not in axios's request `config`. Surfacing the
    // config as `details` made every caller that renders `error.details`
    // either show nothing or crash React with "Objects are not valid as a
    // React child". Carry the backend detail string/array in `details` and the
    // full body in `service`.
    const body: any = response.response?.data;
    error = {
      message: response.message,
      details: body?.details ?? body?.error ?? undefined,
      service: body,
      stack: response.stack,
      status: response.status || 500,
    };
  } else if (isAnketaError(response)) {
    status = response.status;
    success = false;
    error = {
      message: response.message,
      details: response.details,
      service: response.service,
      stack: response.stack,
      status: response.status,
    };
  } else {
    success = true;
    data = response.data;
    status = response.status;
    if (
      !response.data ||
      (Array.isArray(response.data) && response.data.length === 0)
    ) {
      status = 204;
      data = null;
    }
  }

  if (status === 200 || status === 201) {
    logState = "success";
  }
  if (status === 204) {
    logState = "warn";
  }
  if (status === 401 || status === 403) {
    logState = "error";
    success = false;
  }
  const result: AnketaResponse<T> = {
    success,
    data: data,
    status,
    error,
  };

  return result;
};
